import type {
  MontillaDestination,
  EventPayload,
  MontillaIdentifyPayload,
  MontillaLogPayload,
  MontillaErrorPayload,
  MontillaContext,
} from './types/destination'
import type { MontillaLoadConfig } from './types'
import { MiddlewarePipeline, type MiddlewareFn } from './middleware/pipeline'
import { generateUUID } from './utils/uuid'

export class EventBus {
  private destinations: MontillaDestination[] = []
  private initializedNames = new Set<string>()
  private readyPromise: Promise<void> | null = null
  private resolveReady: (() => void) | null = null
  private initialized = false
  private pipeline = new MiddlewarePipeline()

  constructor() {
    this.readyPromise = new Promise<void>((resolve) => {
      this.resolveReady = resolve
    })
  }

  /** Register a destination */
  register(destination: MontillaDestination): void {
    this.destinations.push(destination)
  }

  /** Register a middleware */
  use(fn: MiddlewareFn): void {
    this.pipeline.use(fn)
  }

  /**
   * Initialize a single destination *before* `initAll()`. Used to bootstrap
   * Faro early so other destinations can read `faro.metas.value` during
   * their own init. `initAll()` skips destinations already initialized here.
   */
  async initOne(
    destination: MontillaDestination,
    config: MontillaLoadConfig,
    context: MontillaContext,
  ): Promise<void> {
    if (this.initializedNames.has(destination.name)) return
    try {
      await destination.init(config, context)
    } catch (err) {
      console.error(`[montilla] Destination "${destination.name}" init failed:`, err)
    }
    this.initializedNames.add(destination.name)
  }

  async initAll(config: MontillaLoadConfig, context: MontillaContext): Promise<void> {
    const pending = this.destinations.filter(d => !this.initializedNames.has(d.name))
    const results = await Promise.allSettled(
      pending.map(d => d.init(config, context))
    )

    for (let i = 0; i < pending.length; i++) {
      const result = results[i]
      const dest = pending[i]
      if (!dest) continue
      if (result?.status === 'rejected') {
        console.error('[montilla] Destination init failed:', result.reason)
      } else {
        this.initializedNames.add(dest.name)
      }
    }

    this.initialized = true
    this.resolveReady?.()
    this.resolveReady = null
  }

  async ready(): Promise<void> {
    return this.readyPromise ?? Promise.resolve()
  }

  /**
   * Abre o portão do `dispatch` sem ter completado a inicialização.
   *
   * `dispatch()` espera em `ready()`, e `initAll()` é a ÚLTIMA coisa que
   * `load()` faz: qualquer exceção no meio do caminho — storage bloqueado,
   * import dinâmico 404 depois de um deploy, rede — deixava esta promise
   * pendente PARA SEMPRE. O efeito era o pior possível: nenhum `track()`
   * rejeitava nem resolvia, então os eventos não eram descartados, ficavam
   * presos num await, sem erro e sem log. Não dava para descobrir olhando o
   * console; parecia que o SDK simplesmente não coletava.
   *
   * `load()` chama isto no seu tratamento de erro. Os eventos seguem para os
   * destinos que CONSEGUIRAM inicializar (nenhum, no pior caso) em vez de
   * travar o chamador. Não marca o bus como inicializado: o estado continua
   * sendo "falhou", só deixa de ser silencioso.
   */
  releaseReady(): void {
    this.resolveReady?.()
    this.resolveReady = null
  }

  async dispatch(event: EventPayload): Promise<void> {
    await this.ready()

    // Run through middleware pipeline (user-registered only — built-ins gone)
    const processed = await this.pipeline.execute(withEventId(event))
    if (!processed) return

    const accepting = this.destinations.filter(d => !d.accepts || d.accepts(processed))
    await Promise.allSettled(accepting.map(d => d.track(processed)))
  }

  async dispatchIdentify(payload: MontillaIdentifyPayload, context: MontillaContext): Promise<void> {
    await this.ready()

    await Promise.allSettled(
      this.destinations
        .filter(d => d.identify)
        .map(d => d.identify?.(payload, context))
    )
  }

  async dispatchLog(payload: MontillaLogPayload): Promise<void> {
    await this.ready()

    await Promise.allSettled(
      this.destinations
        .filter(d => d.log)
        .map(d => d.log?.(payload))
    )
  }

  async dispatchError(payload: MontillaErrorPayload): Promise<void> {
    await this.ready()

    await Promise.allSettled(
      this.destinations
        .filter(d => d.error)
        .map(d => d.error?.(payload))
    )
  }

  getDestinations(): MontillaDestination[] {
    return [...this.destinations]
  }

  getDestination<T extends MontillaDestination>(name: string): T | undefined {
    return this.destinations.find(d => d.name === name) as T | undefined
  }

  destroy(): void {
    this.destinations.forEach(d => d.destroy?.())
    this.destinations = []
  }
}

/**
 * Identidade única por evento, carimbada no ÚNICO ponto por onde todos passam.
 *
 * Vive em `attributes` de propósito: o Faro só carrega nome + attributes até o
 * coletor, e os mappings das destinations também leem de lá — fora dali o id
 * não chegaria a lugar nenhum. Com ele, o mesmo evento pode ser reconhecido em
 * sistemas diferentes (coletor, Insider, painel da extensão) e uma reentrega
 * deixa de virar duas linhas.
 *
 * Não sobrescreve um `event_id` que já venha pronto: quem reenvia um evento
 * quer justamente manter o id original.
 */
function withEventId(event: EventPayload): EventPayload {
  const attributes = event.attributes ?? {}
  if (typeof attributes.event_id === 'string' && attributes.event_id) return event
  return { ...event, attributes: { ...attributes, event_id: generateUUID() } }
}
